<template>
  <div class="so-summary-table">
    <h5
      v-if="title"
      class="my-4 d-flex"
      :tabindex="collapsable ? '0' : undefined"
      :role="collapsable ? 'button' : undefined"
      @click="toggle()"
      @keyup.enter="toggle()"
    >
      <span class="text-h5" data-testid="so-summary-table-title">
        {{ title }}
      </span>
      <div
        v-if="$slots.headerRight || collapsable"
        class="ml-auto"
        data-testid="so-summary-table-header-right-slot"
      >
        <slot name="headerRight">
          <SoIcon
            v-if="collapsable"
            :icon="dropdownIcon"
            aria-label="Show more details"
            data-testid="so-summary-table-toggle"
          />
        </slot>
      </div>
    </h5>

    <v-expand-transition>
      <div v-show="isOpen">
        <table
          v-if="!loading"
          class="so-summary-table__table"
          data-testid="so-summary-table-table"
          :summary="title"
        >
          <th
            v-for="(header, index) in ariaHeaders"
            :key="index"
            class="d-none"
          >
            {{ header }}
          </th>
          <SoSummaryTableRow
            v-for="(item, index) in items"
            :key="index"
            :item="item"
          />
          <tr v-if="$slots.footer" class="so-summary-table__row--footer">
            <td colspan="2" class="py-3">
              <slot name="footer"></slot>
            </td>
          </tr>
        </table>
        <SoSkeleton v-else :type="lines" />
      </div>
    </v-expand-transition>

    <table
      v-if="!loading && summary"
      class="so-summary-table__table"
      data-testid="so-summary-table-summary"
      :summary="title"
    >
      <th v-for="(header, index) in ariaHeaders" :key="index" class="d-none">
        {{ header }}
      </th>
      <SoSummaryTableRow :item="summary" summary />
    </table>
    <SoSkeleton v-else-if="summary" width="100%" height="2.5rem" />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { SoSummaryTableItem } from "@/types/components/SoSummaryTable"
  import SoSummaryTableRow from "@/components/SoSummaryTableRow.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoIcon from "@/components/SoIcon.vue"

  export default defineComponent({
    components: {
      SoSummaryTableRow,
      SoSkeleton,
      SoIcon,
    },
    inheritAttrs: false,
    props: {
      title: {
        type: String,
        default: "",
      },
      items: {
        type: Array as PropType<SoSummaryTableItem[]>,
        required: true,
      },
      summary: {
        type: Object as PropType<SoSummaryTableItem>,
        default: undefined,
      },
      collapsable: {
        type: Boolean,
        default: false,
      },
      open: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      ariaHeaders: {
        type: Array as PropType<String[]>,
        default: null,
      },
    },
    data() {
      return {
        isOpen: !this.collapsable || this.open,
      }
    },

    computed: {
      dropdownIcon() {
        return this.isOpen ? "chevron-up" : "chevron-down"
      },
      lines() {
        return new Array(this.items.length).fill("list-item")
      },
    },
    methods: {
      toggle() {
        if (!this.collapsable) return
        this.isOpen = !this.isOpen
      },
    },
  })
</script>

<style scoped>
  .so-summary-table__table {
    table-layout: fixed;
    width: 100%;
    border-spacing: 0;
  }
  .so-summary-table__row--footer td {
    border-top: 1px solid rgb(var(--v-theme-border));
  }
</style>
